import {
  aliasOf,
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";

import {
  isRTL,
  messageBundle,
  storeNode,
  tsx,
} from "@arcgis/core/widgets/support/widget";

import Widget from "@arcgis/core/widgets/Widget";

import ContentAreaViewModel from "./ContentArea/ContentAreaViewModel";

import "./ContentArea/styles/ContentArea.scss";

import AppTypeTile, { AppTypeTileProperties } from "../widgets/AppTypeTile";

import { watch, when } from "@arcgis/core/core/reactiveUtils";
import AppTile from "../widgets/AppTile";
import NavBarViewModel, { navbarRoute } from "./NavBar/NavBarViewModel";
import AppTypeInfoViewModel from "../data/AppTypeInfo/AppTypeInfoViewModel";
import { VNode } from "src/interfaces/interfaces";
import Handles from "@arcgis/core/core/Handles";

import ContentAreaT9N from "../../t9n/ui/ContentArea/ContentArea.json";
import NavBarT9N from "../../t9n/ui/NavBar/NavBar.json";
import AppT9N from "../../t9n/App/App.json";

import Telemetry from "@esri/telemetry";

import Sidebar from "../widgets/Sidebar";
import { EAppTemplateType } from "templates-common-library/baseClasses/CompatibilityChecker";
import ResizeObserver from "resize-observer-polyfill";
import { keyboardAccessibleHelper } from "../utils/appUtils";
import FeaturedArticle from "./FeaturedArticle";
import { capabilitySearchMapping } from "../widgets/SearchBox/capabilitySearchMappings";
import RightSidebar from "../widgets/RightSidebar";

export interface ContentAreaProperties extends __esri.WidgetProperties {
  name?: string;
  view?: __esri.MapView | __esri.SceneView;
  telemetry: Telemetry;
}

@subclass("Landing.ui.ContentArea")
export default class ContentArea extends Widget {
  @aliasOf("viewModel.view")
  view: __esri.MapView | __esri.SceneView;

  @property()
  @messageBundle("dist/assets/t9n/ui/ContentArea/ContentArea")
  messages: typeof ContentAreaT9N;

  @property()
  @aliasOf("viewModel.telemetry")
  telemetry: Telemetry;

  @property()
  @messageBundle("dist/assets/t9n/ui/NavBar/NavBar")
  navbarMessages: typeof NavBarT9N;

  @property()
  @aliasOf("viewModel.appMessages")
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof AppT9N;

  @property()
  apps: AppTypeTileProperties[];

  @property()
  @aliasOf("viewModel.portal")
  portal: __esri.Portal;

  /** Original data arrays */
  @property()
  @aliasOf("viewModel.browseAllTiles")
  browseAllTiles: AppTypeTile[];
  @property()
  featuredTiles: AppTypeTile[];
  @property()
  myAppsTiles: AppTile[];

  /** Views are arrays that have been sorted and filtered.
   * The references to the Tiles are consistent to original data arrays (Tiles aren't copied) */
  @property()
  browseAllTilesView: AppTypeTile[];
  @property()
  featuredTilesView: AppTypeTile[];
  @property()
  myAppsTilesView: AppTile[];

  @property()
  @aliasOf("viewModel.navbarVM")
  navbarVM: NavBarViewModel;

  @property({
    type: ContentAreaViewModel,
  })
  viewModel: ContentAreaViewModel = new ContentAreaViewModel();

  @property()
  _contentArea: HTMLDivElement;
  @property()
  _contentOverlay: HTMLDivElement;
  @property()
  _handles: Handles = new Handles();
  @property()
  featuredArticle: FeaturedArticle = new FeaturedArticle({});

  private _sideBar: Sidebar = new Sidebar();
  private _rightSideBar: RightSidebar = new RightSidebar();

  private _contentOverlayObserver = new ResizeObserver(
    this._recalcTileStyles.bind(this)
  );

  private viewState: { state: "detailed" | "thumbnail" } = {
    state: "thumbnail",
  };

  constructor(properties?: ContentAreaProperties) {
    super(properties);
  }

  postInitialize() {
    this.featuredArticle.navbarVM = this.navbarVM;
    this._sideBar.navbarVM = this.navbarVM;
    this._rightSideBar.navbarVM = this.navbarVM;
    const { viewModel } = this;
    const { appTypeInfoVM } = viewModel;

    when(
      () => appTypeInfoVM?.isAppTypeDataLoaded === true,
      () => {
        if (this.viewModel.itemLoaderVM.loadedItemType === "none") {
          this._initialAppTemplateSetup(appTypeInfoVM);
        } else {
          when(
            () => viewModel?.notCompatible !== undefined,
            () => {
              if (viewModel?.notCompatible != undefined) {
                this._initialAppTemplateSetup(appTypeInfoVM);
              }
            },
            {
              initial: true,
              once: true,
            }
          );
        }
      },
      {
        initial: true,
        once: true,
      }
    );

    when(
      () => this?._contentOverlay !== undefined,
      () => {
        this._contentOverlayObserver.observe(this._contentOverlay);
      },
      {
        initial: true,
        once: true,
      }
    );

    this._handles.add(
      watch(
        () => this?.viewModel?.myAppsDataVM?.myApps,
        () => {
          this.myAppsTiles = this._generateMyAppTiles(
            this?.viewModel?.myAppsDataVM?.myApps,
            this.viewModel
          );
          if (this.navbarVM.navigationRoute === "myApps") {
            this._generateViewForAppTiles(this.myAppsTiles, "myAppsTilesView");
          }
        }
      )
    );

    this._handles.add(
      this.navbarVM.watch("navigationRoute", () => {
        this._resetScrollViewToTop();
      })
    );

    this._handles.add(
      this.navbarVM.watch(
        [
          "sortField",
          "navigationRoute", // browse, feat and myApps
          "appStringSearch", // myApps only
        ],
        () => {
          this._recalcTileStyles();
          this._recalculateView();
        }
      )
    );

    this._handles.add(
      this.navbarVM.filterByList.on("change", () => {
        this._recalculateView();
      })
    );

    this._handles.add(
      this._sideBar.watch("filteredTemplateList", () => {
        this._recalculateView();
      })
    );
  }

  render() {
    const tiles = this._renderNavBarRoute();
    return (
      <div
        class="app-layout__content-area"
        bind={this}
        afterCreate={storeNode}
        data-node-ref={"_contentArea"}
      >
        {this?._sideBar?.render()}

        <div id="content-area">
          <div
            id="map-area"
            afterCreate={this.viewModel.createMap.bind(this.viewModel)}
          ></div>
          {!this?.viewModel?.navbarVM?.suggestionsButtonSignal &&
            this._renderSidebarToggle()}

          <div
            bind={this}
            afterCreate={storeNode}
            data-node-ref="_contentOverlay"
            class={this.classes(
              `content-overlay`,
              `${
                this.navbarVM.navigationRoute === "myApps"
                  ? "content-overlay--myApps"
                  : ""
              }`,
              `${
                this.navbarVM.navigationRoute === "featured"
                  ? "content-overlay--featured"
                  : ""
              }`
            )}
          >
            {this._renderTileArea(tiles)}
          </div>

          {!this?.viewModel?.navbarVM?.rightSidebarSignal &&
            this.navbarVM.navigationRoute === "featured" &&
            this._renderRightSidebarToggle()}
        </div>
        <div>{this._renderAlertNotification()}</div>

        {this?._rightSideBar?.render()}
      </div>
    );
  }

  destroy() {
    this._handles.removeAll();
  }

  setHeight(height: number): void {
    this._contentArea.style.height = `${height}px`;
  }

  /**
   * Note: not using media queries here because the collapsible sidebars
   * aren't taken into account by the media queries
   */
  private _recalcTileStyles() {
    const w = this?._contentOverlay?.clientWidth;

    if (w == null) {
      return;
    }

    const tilesContArea = document.getElementsByClassName("tiles");
    for (let x = 0; x < tilesContArea.length; x++) {
      const contArea = tilesContArea[x];
      const clearClasses = () => {
        contArea.classList.remove(
          ...["mark-large", "mark-medium", "mark-small", "mark-xsmall"]
        );
      };

      if (1600 < w) {
        clearClasses();
      }
      if (1275 < w && w <= 1600) {
        clearClasses();
        contArea.classList.add("mark-large");
      } else if (1090 < w && w <= 1275) {
        clearClasses();
        contArea.classList.add("mark-medium");
      } else if (600 < w && w <= 1090) {
        clearClasses();
        contArea.classList.add("mark-small");
      } else if (w <= 600) {
        clearClasses();
        contArea.classList.add("mark-xsmall");
      }
    }
  }

  private _initialAppTemplateSetup(appTypeInfoVM: AppTypeInfoViewModel) {
    // calculate browse all tab apps
    const browseAllApps = appTypeInfoVM.appTypeInfo.map((item, index) => {
      return this.viewModel.formatAppTypeItems(item, index);
    });
    this.browseAllTiles = this._generateAppTypeTiles(browseAllApps);
    this._generateViewForAppTypeTiles(
      this.browseAllTiles,
      "browseAllTilesView"
    );

    // calculate featured tab apps
    const featuredApps = appTypeInfoVM.appTypeInfo
      .filter((item: __esri.PortalItem) => {
        return this.viewModel.filterFeaturedApps(item);
      })
      .map((item, index) => {
        return this.viewModel.formatAppTypeItems(item, index);
      });

    this.featuredTiles = this._generateAppTypeTiles(featuredApps);
    this._generateViewForAppTypeTiles(this.featuredTiles, "featuredTilesView");

    this.scheduleRender();
  }

  private _recalculateView() {
    switch (this.navbarVM.navigationRoute) {
      case "browseAll":
        this._generateViewForAppTypeTiles(
          this.browseAllTiles,
          "browseAllTilesView"
        );
        return;
      case "featured":
        this._generateViewForAppTypeTiles(
          this.featuredTiles,
          "featuredTilesView"
        );
        return;
      case "myApps":
        this._generateViewForAppTiles(this.myAppsTiles, "myAppsTilesView");
        return;
    }
  }

  /** Preforms sort and filter on AppTypeTiles, and stores result in a dataview */
  private _generateViewForAppTypeTiles(
    data: AppTypeTile[],
    viewField: string
  ): void {
    let viewArray: AppTypeTile[] = this.viewModel.createView(
      data
    ) as AppTypeTile[];

    const sortField = this.navbarVM.sortField;
    if (sortField) {
      this.navbarVM.sortTiles(sortField, viewArray);
    }

    const suggestionsFilterList = this?._sideBar?.filteredTemplateList;

    if (
      suggestionsFilterList != null &&
      this.navbarVM.suggestionsButtonSignal
    ) {
      viewArray = viewArray.filter((tile: AppTypeTile) => {
        return suggestionsFilterList.includes(
          tile.appTypeInfo.appTypeUrlFragment
        );
      });
    } else if (this.navbarVM.filterByList.length > 0) {
      viewArray = viewArray.filter((tile: AppTypeTile) => {
        return this.navbarVM.filterByList
          .map(([translationKey, filterTag]: [string, string]) => {
            // does App template's tags contain the filterTag?
            return (
              capabilitySearchMapping[
                tile.appTypeInfo.appTypeUrlFragment
              ]?.findIndex((tag) => translationKey === tag) !== -1
            );
          })
          .reduce((acc, curr) => {
            return acc && curr;
          }); // AND all bools together, if true then app template tag's contain all filterTags
      });
    }

    this[viewField] = viewArray;
  }

  /** Preforms sort and filter on AppTiles, and stores result in a dataview */
  private _generateViewForAppTiles(data: AppTile[], viewField: string): void {
    let viewArray: AppTile[] = this.viewModel.createView(data) as AppTile[];

    this[viewField] = viewArray;
  }

  private _renderNavBarRoute(): any[] {
    switch (this.navbarVM.navigationRoute) {
      case "browseAll":
        return this.browseAllTilesView?.map((tile) => tile?.render());
      case "featured":
        return this.featuredTilesView?.map((tile) => tile?.render());
      case "myApps":
        return this.myAppsTilesView?.map((tile) => tile?.render());
    }
  }

  private _renderTileArea(tiles: VNode[]): VNode {
    switch (this.navbarVM.navigationRoute) {
      case "browseAll":
        if (
          !this.viewModel.appTypeInfoVM.isAppTypeDataLoaded ||
          !this.viewModel.itemLoaderVM.isItemLoaded
        ) {
          return this._renderLoadingScreen();
        } else if (!tiles || tiles.length === 0) {
          return this._renderNoResults(this.navbarVM.navigationRoute);
        }
        return [
          // render tile view switcher
          this._renderCardViewSwitcher(),
          // render tiles
          this.viewModel.itemLoaderVM.loadedItemType !== "none" &&
          this.navbarVM.sortField === "compatibleMsgs" ? (
            this._renderCompatibilityTileSort()
          ) : (
            <div
              afterCreate={this._assertTileStyling.bind(this)}
              class={this.classes(
                "tiles",
                this.viewState.state === "detailed" ? "tiles--detailed" : null
              )}
              key="tilesBrowseAll"
            >
              {...tiles}
            </div>
          ),
        ];
      case "featured":
        return this?.featuredArticle?.render();
      case "myApps":
        if (
          !this.viewModel.myAppsDataVM.isMyAppsLoading &&
          (!tiles || tiles.length === 0)
        ) {
          return this._renderNoResults(this.navbarVM.navigationRoute);
        }
        return [
          <div key="tiles-myApps" class="tiles-myApps">
            {this.viewModel.myAppsDataVM.isMyAppsLoading ? (
              this._renderLoadingScreen()
            ) : (
              <div
                afterCreate={this._assertTileStyling.bind(this)}
                class="tiles"
                key="tilesMyApps"
              >
                {tiles}
              </div>
            )}
          </div>,
          this._renderPagination(),
        ];
    }
  }

  private _renderPagination() {
    const { num, start, totalFromQuery } = this.viewModel.myAppsDataVM;

    return totalFromQuery > num ? (
      <calcite-pagination
        key="tiles-pagination"
        class="pagination-footer"
        startItem={start}
        pageSize={num}
        totalItems={totalFromQuery}
        afterCreate={(paginationCtrl: HTMLCalcitePaginationElement) => {
          paginationCtrl.addEventListener(
            "calcitePaginationChange",
            (e: any) => {
              this.viewModel.myAppsDataVM.setStartAndRequery(
                e?.target?.startItem
              );
              this._resetScrollViewToTop();
              this.scheduleRender();
            }
          );
        }}
      ></calcite-pagination>
    ) : null;
  }

  private _generateAppTypeTiles(
    appTypes: AppTypeTileProperties[]
  ): AppTypeTile[] {
    return appTypes.map((prop) => {
      return new AppTypeTile({ ...prop, layoutType: this.viewState });
    });
  }

  private _generateMyAppTiles(
    myApps: __esri.Collection<__esri.PortalItem>,
    contentAreaVM: ContentAreaViewModel
  ): AppTile[] {
    return myApps
      .map((appInfo) => {
        return new AppTile({ appInfo, portal: this.portal, contentAreaVM });
      })
      .toArray();
  }

  private _renderNoResults(tab: navbarRoute): VNode {
    const {
      noResults,
      noResultsDescPart1,
      noResultsDescPart2,
      noResultsDescPart3,
      noMyApps_Title,
    } = this.appMessages.searchResults;

    if (tab === "browseAll" || tab === "featured") {
      const { BrowseAll, Featured } = this.navbarMessages;
      return (
        <div class="no-results">
          <calcite-card class="no-results__card">
            <h3 class="no-results__card-title" slot="title">
              {noResults}
            </h3>
            <span class="no-results__card-content" slot="subtitle">
              {`${noResultsDescPart1} ${
                tab === "browseAll" ? BrowseAll : Featured
              } ${noResultsDescPart2}. ${noResultsDescPart3}`}
            </span>
          </calcite-card>
        </div>
      );
    } else if (tab === "myApps") {
      const { MyApps } = this.navbarMessages;
      return (
        <div class={this.classes("no-results", "no-results--myApps")}>
          <calcite-card class="no-results__card">
            <h3 class="no-results__card-title" slot="title">
              {this._isNoResults() ? noResults : noMyApps_Title}
            </h3>
            <span class="no-results__card-content" slot="subtitle">
              {this._isNoResults()
                ? `${noResultsDescPart1} ${MyApps} ${noResultsDescPart2}. ${noResultsDescPart3}`
                : this._renderNoInstantAppsMsg()}
            </span>
          </calcite-card>
        </div>
      );
    }
  }

  private _isNoResults(): boolean {
    const { totalFromQuery, filterText } = this.viewModel.myAppsDataVM;
    return totalFromQuery === 0 && (filterText != null || filterText != "");
  }

  private _renderAlertNotification(): VNode[] {
    return this.viewModel.activeAlerts
      ?.map((alert) => alert?.render())
      .toArray();
  }

  private _resetScrollViewToTop() {
    if (this._contentOverlay) {
      this._contentOverlay.scrollTop = 0;
    }
  }

  /**
   * In "Browse All", when sorted by compatibility, and there is no capability search, then use this
   * view (of 2 rows, one with compatible apps, the other with not compatible) to show the App Template Tiles.
   */
  private _renderCompatibilityTileSort(): VNode {
    const compat: any[] = this.browseAllTilesView
      ?.filter((tile) => !tile?.appTypeInfo?.compatibleMsgs)
      ?.map((tile) => tile?.render());
    const notCompat: any[] = this.browseAllTilesView
      ?.filter((tile) => tile?.appTypeInfo?.compatibleMsgs)
      ?.map((tile) => tile?.render());
    const detailedViewClass =
      this.viewState.state === "detailed" ? "tiles--detailed" : null;
    return (
      <div key="compat-tiles" class="compat-tiles">
        <div
          afterCreate={this._assertTileStyling.bind(this)}
          class={this.classes("tiles", detailedViewClass)}
          id="compat"
        >
          {...compat}
        </div>

        {notCompat.length > 0 ? (
          <div
            class="content-area-section-header"
            key="additional-apps-section-header"
          >
            <h2 id="additionalApps" class="additional-apps-header" tabindex="0">
              {this.appMessages.additionalApps.header}
            </h2>
            <calcite-tooltip
              reference-element="additionalApps"
              placement="top"
              open={false}
            >
              {this.appMessages.additionalApps.tip}
              {this._decideResourceTypeStr()}
            </calcite-tooltip>
          </div>
        ) : null}
        <div
          afterCreate={this._assertTileStyling.bind(this)}
          class={this.classes("tiles", detailedViewClass)}
          id="notCompat"
        >
          {...notCompat}
        </div>
      </div>
    );
  }

  private _decideResourceTypeStr(): string {
    const { webmap, webscene, group } = this.appMessages.resourceTypes;
    switch (this.viewModel.itemLoaderVM.loadedItemType) {
      case "webmap":
        return webmap;
      case "webscene":
        return webscene;
      case "group":
        return group;
    }
  }

  private _renderLoadingScreen(): VNode {
    return (
      <div class="loader-div">
        <calcite-loader></calcite-loader>
      </div>
    );
  }

  /** Loading error that shows when the webmap/webscene's layers have a problem loading */
  private _renderLoadingError(): VNode {
    const { title, description } =
      this?.appMessages?.resourceLoadingError || {};
    return (
      <div
        class={this.classes(
          "no-results",
          this.navbarVM.navigationRoute === "myApps"
            ? "no-results--myApps"
            : null
        )}
      >
        <calcite-card class="no-results__card">
          <h3 class="no-results__card-title" slot="title">
            {title}
          </h3>
          <span class="no-results__card-content" slot="subtitle">
            {description}
          </span>
        </calcite-card>
      </div>
    );
  }

  private _renderNoInstantAppsMsg(): VNode {
    const { noMyApps_Desc_1, noMyApps_Desc_2, noMyApps_Desc_4 } =
      this.appMessages.searchResults;
    const { and } = this.appMessages.generalUse.conjunctions;
    return (
      <span class="no-instant-apps-msg">
        {noMyApps_Desc_1}&nbsp;
        <a
          tabindex="0"
          onclick={() => {
            this.navbarVM.setNavbarRoute("browseAll");
          }}
          onkeydown={(e) => {
            if (e.code === "Enter" || e.code === "Space") {
              this.navbarVM.setNavbarRoute("browseAll");
            }
          }}
        >
          {noMyApps_Desc_2}
        </a>
        &nbsp;
        {noMyApps_Desc_4}
      </span>
    );
  }

  private _renderSidebarToggle(): VNode {
    const { open, close } = this.appMessages.suggestions;
    return (
      <div key="sidebar-floating-button" class="sidebar-floating-button">
        <div
          class="sidebar-toggle"
          role="button"
          tabindex="0"
          title={this.navbarVM.suggestionsButtonSignal ? close : open}
          onkeydown={(e) => {
            keyboardAccessibleHelper(
              this.navbarVM.toggleSuggBtnSignal.bind(this.navbarVM),
              e
            );
          }}
          onclick={() => this.navbarVM.toggleSuggBtnSignal()}
        >
          <calcite-icon
            icon={
              isRTL()
                ? this.navbarVM.suggestionsButtonSignal
                  ? "chevron-right"
                  : "chevron-left"
                : this.navbarVM.suggestionsButtonSignal
                ? "chevron-left"
                : "chevron-right"
            }
            scale="l"
          ></calcite-icon>
        </div>
      </div>
    );
  }

  private _renderRightSidebarToggle(): VNode {
    const { open, close } = this.appMessages.suggestions;
    return (
      <div
        key="right-sidebar-floating-button"
        class={this.classes(
          "sidebar-floating-button",
          "right-sidebar-floating-button"
        )}
      >
        <div
          class="right-sidebar-toggle"
          role="button"
          tabindex="0"
          title={this.navbarVM.rightSidebarSignal ? close : open}
          onkeydown={(e) => {
            keyboardAccessibleHelper(
              this.navbarVM.toggleRightSidebarSignal.bind(this.navbarVM),
              e
            );
          }}
          onclick={() => this.navbarVM.toggleRightSidebarSignal()}
        >
          <calcite-icon
            icon={
              isRTL()
                ? this.navbarVM.rightSidebarSignal
                  ? "chevron-left"
                  : "chevron-right"
                : this.navbarVM.rightSidebarSignal
                ? "chevron-right"
                : "chevron-left"
            }
            scale="l"
          ></calcite-icon>
        </div>
      </div>
    );
  }

  /**
   * Recalcs grid style on "tiles" because sometimes
   * it gets messed up due to race condition
   * Issue ref: https://devtopia.esri.com/WebGIS/arcgis-template-landing/issues/394
   */
  private _assertTileStyling(): void {
    setTimeout(() => {
      this._recalcTileStyles();
    });
  }

  private _renderCardViewSwitcher(): VNode {
    const detailsView = this.appMessages?.switcherLabels?.detailsView;
    const galleryView = this.appMessages?.switcherLabels?.galleryView;
    return (
      <div key="view-switcher-key" class="view-switcher">
        <div
          class={this.classes(
            "switcher-icon",
            this.viewState.state === "thumbnail" ? "selected-switcher" : null
          )}
          onclick={() => {
            this.viewState.state = "thumbnail";
          }}
          onkeyup={(e) => {
            keyboardAccessibleHelper(() => {
              this.viewState.state = "thumbnail";
            }, e);
          }}
          tabindex="0"
          id="grid-icon"
        >
          <calcite-icon icon="grid"></calcite-icon>
        </div>

        <calcite-tooltip
          placement="auto"
          label={galleryView}
          reference-element="grid-icon"
          offset-distance="3"
          offset-skidding="10"
        >
          <span>{galleryView}</span>
        </calcite-tooltip>

        <div
          class={this.classes(
            "switcher-icon",
            this.viewState.state === "detailed" ? "selected-switcher" : null
          )}
          onclick={() => {
            this.viewState.state = "detailed";
          }}
          onkeyup={(e) => {
            keyboardAccessibleHelper(() => {
              this.viewState.state = "detailed";
            }, e);
          }}
          tabindex="0"
          id="details-icon"
        >
          <calcite-icon icon="list-bullet"></calcite-icon>
        </div>

        <calcite-tooltip
          placement="auto"
          label={detailsView}
          reference-element="details-icon"
          offset-distance="3"
          offset-skidding="10"
        >
          <span>{detailsView}</span>
        </calcite-tooltip>
      </div>
    );
  }
}
